import { useDeferredValue, useEffect, useMemo, useState } from "react";
import useUserStore from "../../../services/userStore";
import { myLocalStorage } from "../../StorageHelper";
import { fetchRecordingDetailsAPI, getToken, saveToken } from "./Api/recordingapi";
import { useToast } from "../../../hooks/use-toast";
import { mapConnection } from "./connectionHelper";
import { Button } from "../../ui/button";
import { CalendarIcon, Clock, Download, Play, Search, User } from "lucide-react";
import { Input } from "../../ui/input";
import { Card, CardContent } from "../../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { ConnectionLog } from "./ConnectionInterface";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { cn } from "../../../utils/utils";
import { Skeleton } from "../../ui/skeleton";
import moment from "moment";
import { Calendar } from "../../ui/calendar";

interface RecordingDetailsProps {
  computerName: string;
  deviceId: string;
  guachost: string;
}

export default function RecordingDetails({ computerName = "", deviceId = "", guachost = "" }: RecordingDetailsProps) {
  const tenantName = myLocalStorage.getItem("latestTenant").tenantName || "";
  const { email } = useUserStore((state) => state.user);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [connections, setConnections] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const itemsPerPage = 10;
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const fetchRecordingDetails = async () => {
    try {
      setLoading(true);
      const response = await fetchRecordingDetailsAPI(deviceId);
      const formatted = response
        .map(mapConnection)
        .sort((a, b) => {
          const aEnd = moment(a.endDate, "YYYY-MM-DD HH:mm");
          const bEnd = moment(b.endDate, "YYYY-MM-DD HH:mm");

          if (aEnd.isSame(bEnd)) {
            const aStart = moment(a.startDate, "YYYY-MM-DD HH:mm");
            const bStart = moment(b.startDate, "YYYY-MM-DD HH:mm");
            return bStart.diff(aStart);
          }
          return bEnd.diff(aEnd);
        });
      setConnections(formatted);
    } catch (error) {
      console.error("Error fetching recording details:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCredentials = useMemo(() => {
    if (!deferredSearchTerm.trim() && !startDate && !endDate) {
      return connections;
    }

    const query = deferredSearchTerm.toLowerCase().trim();

    const toDateOnly = (date: Date | null) => {
      if (!date) return null;
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    return connections.filter((connection) => {
      const userValue = connection?.user?.toString().toLowerCase() || "";
      const matchesSearch = query ? userValue.includes(query) : true;

      const start = toDateOnly(startDate ? new Date(startDate) : null);
      const end = toDateOnly(endDate ? new Date(endDate) : null);
      const connStart = toDateOnly(connection?.startDate ? new Date(connection.startDate) : null);
      const connEnd = toDateOnly(connection?.endDate ? new Date(connection.endDate) : null);

      let matchesDate: boolean = true;

      if (start && end) {
        const isSameDay = start.getTime() === end.getTime();

        if (isSameDay) {
          matchesDate = Boolean(
            (connStart && connStart.getTime() === start.getTime()) ||
            (connEnd && connEnd.getTime() === end.getTime())
          );
        } else {
          matchesDate = Boolean(
            connStart &&
            connEnd &&
            connEnd >= start &&
            connStart <= end
          );
        }
      } else if (start) {
        matchesDate = Boolean(connEnd && connEnd >= start);
      } else if (end) {
        matchesDate = Boolean(connStart && connStart <= end);
      }

      return matchesSearch && matchesDate;
    });
  }, [deferredSearchTerm, startDate, endDate, connections]);




  useEffect(() => {
    fetchRecordingDetails();
  }, [email, tenantName, computerName]);

  useEffect(() => {
    setCurrentPage(1);
  }, [deferredSearchTerm]);

  const totalPages = Math.ceil(filteredCredentials.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRecordings = filteredCredentials.slice(startIndex, endIndex);

  const checkIfViewIsEnabled = (connect: ConnectionLog) => {
    const hasLogs = Object.keys(connect.logs).length > 0;
    const hasValidEndDate = connect.endDate &&
      connect.endDate.trim() !== "" &&
      connect.endDate !== "N/A" &&
      connect.endDate.toLowerCase() !== "invalid date";
    return hasLogs && hasValidEndDate;
  };

  const openConnection = async (connect: ConnectionLog) => {
    try {
      const logString = Object.keys(connect.logs || {})[0];
      if (!logString) return;

      let authToken = (await getToken(email, deviceId, "admin"))?.authToken;
      if (!authToken) {
        authToken = (await saveToken(email, deviceId, "admin"))?.authToken;
      }

      if (authToken) {
        const url = `https://${guachost}/#/settings/postgresql/recording/${connect.identifier}/${logString}?token=${authToken}`;
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Error opening connection:", error);
    }
  };

  const handleDownloadReport = () => {
    toast({
      title: "Download Started",
      description: "Recordings report is being downloaded...",
    });
  };

  const handlePageChange = (page: number) => setCurrentPage(page);

  const formatDuration = (duration: string) => {
    const [h, m, s] = duration.split(":").map(Number);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const getDurationColor = (duration: string) => {
    const [h, m, s] = duration.split(":").map(Number);
    const totalMinutes = h * 60 + m + s / 60;

    if (totalMinutes > 60) return "text-red-600 dark:text-red-400";
    if (totalMinutes >= 15) return "text-yellow-600 dark:text-yellow-400";
    return "text-green-600 dark:text-green-400";
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={i === currentPage ? "default" : "outline"}
          size="sm"
          onClick={() => handlePageChange(i)}
          className={i === currentPage ? "bg-blue-600 hover:bg-blue-700" : ""}
        >
          {i}
        </Button>
      );
    }
    return pages;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-6 space-y-4">
        <div className="flex flex-wrap items-center gap-4">


          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("w-32 justify-start text-left font-normal", !startDate && "text-muted-foreground")}>
                <CalendarIcon className="w-4 h-4 mr-2" />
                {startDate ? moment(startDate).format("YYYY-MM-DD") : "Start Date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("w-32 justify-start text-left font-normal", !endDate && "text-muted-foreground")}>
                <CalendarIcon className="w-4 h-4 mr-2" />
                {endDate ? moment(endDate).format("YYYY-MM-DD") : "End Date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus disabled={(date) => {
                if (!startDate) return true;
                return date < startDate
              }} />
            </PopoverContent>
          </Popover>
          <div className="relative flex-1 max-w-md">
            <Search className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
            <Input placeholder="Search recordings..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
          </div>

        </div>
      </div>

      {/* Table */}
      <div className="flex-1 px-6">
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-800 hover:bg-slate-800">
                  <TableHead className="w-64 font-medium text-white">User</TableHead>
                  <TableHead className="w-48 font-medium text-white">Start Date</TableHead>
                  <TableHead className="w-48 font-medium text-white">End Date</TableHead>
                  <TableHead className="w-32 font-medium text-white">Duration</TableHead>
                  <TableHead className="w-40 font-medium text-white">Remote Host</TableHead>
                  <TableHead className="w-32 font-medium text-white">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="w-40 h-6" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-28" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-28" /></TableCell>
                      <TableCell><Skeleton className="w-16 h-6" /></TableCell>
                      <TableCell><Skeleton className="w-24 h-6" /></TableCell>
                      <TableCell><Skeleton className="w-20 h-8" /></TableCell>
                    </TableRow>
                  ))
                ) : currentRecordings.length > 0 ? (
                  currentRecordings.map((rec) => (
                    <TableRow key={rec.id} className="hover:bg-muted/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full dark:bg-blue-900/20">
                            <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <div className="text-sm font-medium">{rec.user}</div>
                            <div className="text-xs text-muted-foreground">Session Recording</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{rec.startDate}</TableCell>
                      <TableCell className="font-mono text-sm">{rec.endDate}</TableCell>
                      <TableCell>
                        <span className={`font-medium text-sm ${getDurationColor(rec.duration)}`}>{formatDuration(rec.duration)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 text-xs font-medium rounded-md bg-gray-50 dark:bg-gray-900/20">
                          {rec.remoteHost}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button size="sm" onClick={() => openConnection(rec)} disabled={!checkIfViewIsEnabled(rec)} className="bg-blue-600 hover:bg-blue-700">
                          <Play className="w-3 h-3 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      No recordings found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Pagination */}
      {totalPages > 1 && !loading && (
        <div className="flex items-center justify-between px-6 py-4 border-t">
          <div className="flex items-center space-x-2">
            <Button size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              Previous
            </Button>
            {renderPagination()}
            <Button size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
